<template>
  <div class="form-page">
    <header class="form-heading">
      <div>
        <p>Acquisitions</p>
        <h1>Suivre une annonce</h1>
        <span>Ajoutez une enchère à votre suivi à partir de son adresse.</span>
      </div>
    </header>
    <div class="form-content">
      <form class="form-panel" @submit.prevent="parseOffer">
        <section class="form-section">
          <div class="form-section__heading">
            <b>01</b>
            <div>
              <h2>Annonce à surveiller</h2>
              <p>
                Yahoo Auctions et les liens d’enchères ZenMarket sont pris en
                charge.
              </p>
            </div>
          </div>
          <div class="form-section__body">
            <div class="form-field">
              <label for="offer-url">URL de l’annonce</label>
              <div
                class="url-input"
                :class="{ error: urlError, valid: isValidUrl }"
              >
                <input
                  id="offer-url"
                  ref="urlInput"
                  v-model.trim="url"
                  type="url"
                  autocomplete="url"
                  placeholder="https://page.auctions.yahoo.co.jp/jp/auction/..."
                  @input="clearError"
                  @blur="validateUrl"
                /><span v-if="source">{{ source }}</span>
              </div>
              <p class="form-help" :class="{ 'is-error': urlError }">
                {{
                  urlError ||
                  "Collez l’adresse complète de la page de l’enchère."
                }}
              </p>
            </div>
          </div>
        </section>
        <section class="form-section form-section--muted">
          <div class="form-section__heading">
            <b>02</b>
            <div>
              <h2>Rappel de fin d’enchère</h2>
              <p>Vos réglages de notifications s’appliquent à cette annonce.</p>
            </div>
          </div>
          <div class="form-section__body">
            <div class="alert-summary">
              <span class="alert-icon"
                ><font-awesome-icon :icon="['fas', 'bell']"
              /></span>
              <div v-if="alertPrefs">
                <strong>{{ delaysLabel }}</strong
                ><small>{{ channelsLabel }}</small>
              </div>
              <div v-else>
                <strong>{{
                  alertError ? "Réglages indisponibles" : "Chargement…"
                }}</strong>
              </div>
              <em v-if="alertPrefs" :class="{ off: !hasChannel }"
                ><i></i> {{ hasChannel ? "Actif" : "Aucun canal" }}</em
              >
            </div>
            <router-link
              to="/notifications"
              class="form-button form-button--link"
              >Modifier mes réglages de notifications →</router-link
            >
          </div>
        </section>
        <footer class="form-footer">
          <router-link to="/offers" class="form-button form-button--secondary"
            >Annuler</router-link
          ><button
            class="form-button form-button--primary"
            type="submit"
            :disabled="!isValidUrl"
          >
            Ajouter au suivi
          </button>
        </footer>
      </form>
      <section v-if="entries.length" class="queue" aria-live="polite">
        <header class="queue__header">
          <h2>Ajouts de la session</h2>
          <button
            v-if="hasFinished"
            type="button"
            class="queue__clear"
            @click="clearFinishedParses"
          >
            Effacer les terminés
          </button>
        </header>
        <p class="queue__hint">
          L’analyse se poursuit en arrière-plan : vous pouvez continuer à
          ajouter des annonces ou changer de page.
        </p>
        <ul>
          <li
            v-for="entry in entries"
            :key="entry.id"
            :class="['queue__item', `queue__item--${entry.status}`]"
          >
            <span class="queue__dot" aria-hidden="true"></span>
            <div class="queue__body">
              <strong>{{ shortUrl(entry.url) }}</strong>
              <small v-if="entry.status === 'failed'">{{
                entry.error || "L’analyse a échoué."
              }}</small>
              <small v-else-if="entry.status === 'timeout'"
                >L’analyse prend plus de temps que prévu ; l’annonce apparaîtra
                dans la liste une fois terminée.</small
              >
            </div>
            <span class="queue__status">{{ statusLabel(entry) }}</span>
          </li>
        </ul>
        <router-link to="/offers" class="queue__link"
          >Voir mes annonces suivies →</router-link
        >
      </section>
      <BookmarkletCard />
    </div>
  </div>
</template>

<script>
import { computed, nextTick, onActivated, ref } from "vue";
import axios from "axios";
import config from "@/config/config";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import BookmarkletCard from "./BookmarkletCard.vue";
import {
  clearFinishedParses,
  enqueueParse,
  parseQueue,
} from "@/api/parseQueue.js";

export default {
  name: "ParseOffer",
  components: { BookmarkletCard, FontAwesomeIcon },
  setup() {
    const url = ref("");
    const urlError = ref("");
    const urlInput = ref(null);

    const parsedUrl = computed(() => {
      try {
        return new URL(url.value);
      } catch {
        return null;
      }
    });
    const source = computed(() => {
      const host = parsedUrl.value?.hostname || "";
      if (host.includes("yahoo.co.jp")) return "Yahoo Auctions";
      if (host.includes("zenmarket.jp")) return "ZenMarket";
      return "";
    });
    const isValidUrl = computed(() =>
      Boolean(
        parsedUrl.value &&
        source.value &&
        parsedUrl.value.pathname.includes("auction"),
      ),
    );
    const entries = computed(() => parseQueue.entries);

    // End-of-auction reminder: the user's real settings (Notifications page)
    const alertPrefs = ref(null);
    const alertDevices = ref(0);
    const alertError = ref(false);
    const delayLabel = (delay) => (delay === 60 ? "1 h" : `${delay} min`);
    const delaysLabel = computed(() => {
      const labels = (alertPrefs.value?.alert_delays || []).map(delayLabel);
      if (!labels.length) return "";
      const list =
        labels.length > 1
          ? `${labels.slice(0, -1).join(", ")} et ${labels.at(-1)}`
          : labels[0];
      return `${list} avant la fin`;
    });
    const pushActive = computed(
      () => alertPrefs.value?.push_enabled && alertDevices.value > 0,
    );
    const ntfyActive = computed(
      () => alertPrefs.value?.ntfy_enabled && alertPrefs.value?.ntfy_channel,
    );
    const hasChannel = computed(() => pushActive.value || ntfyActive.value);
    const channelsLabel = computed(() => {
      const channels = [];
      if (pushActive.value)
        channels.push(
          `push sur ${alertDevices.value} appareil${alertDevices.value > 1 ? "s" : ""}`,
        );
      if (ntfyActive.value) channels.push("ntfy");
      return channels.length
        ? `Envoyé par ${channels.join(" et ")}`
        : "Aucun canal actif : l’alerte ne s’affichera que dans l’application.";
    });
    const loadAlertPrefs = async () => {
      try {
        const [{ data: prefs }, { data: devices }] = await Promise.all([
          axios.get(`${config.apiUrl}/notifications/preferences`),
          axios.get(`${config.apiUrl}/notifications/devices`),
        ]);
        alertPrefs.value = prefs;
        alertDevices.value = Array.isArray(devices) ? devices.length : 0;
      } catch (error) {
        alertError.value = true;
      }
    };
    // Page kept alive: reread on each visit (settings may have changed)
    onActivated(loadAlertPrefs);
    const hasFinished = computed(() =>
      parseQueue.entries.some((entry) => entry.status !== "pending"),
    );

    const validateUrl = () => {
      urlError.value = !url.value
        ? "Saisissez le lien de l’annonce."
        : !isValidUrl.value
          ? "Cette URL Yahoo Auctions ou ZenMarket n’est pas reconnue."
          : "";
      return !urlError.value;
    };
    const clearError = () => {
      urlError.value = "";
    };

    // Ajout non bloquant : l'analyse continue en arrière-plan, on peut enchaîner
    const parseOffer = async () => {
      if (!validateUrl()) return;
      enqueueParse(url.value, true);
      url.value = "";
      await nextTick();
      urlInput.value?.focus();
    };

    const shortUrl = (value) => {
      try {
        const parsed = new URL(value);
        return (
          parsed.searchParams.get("itemCode") ||
          parsed.pathname.split("/").filter(Boolean).pop() ||
          value
        );
      } catch {
        return value;
      }
    };
    const statusLabel = (entry) =>
      ({
        pending: "Analyse en cours…",
        done: "Ajoutée",
        failed: "Échec",
        timeout: "Toujours en cours",
      })[entry.status];

    return {
      alertError,
      alertPrefs,
      channelsLabel,
      delaysLabel,
      hasChannel,
      clearError,
      clearFinishedParses,
      entries,
      hasFinished,
      isValidUrl,
      parseOffer,
      shortUrl,
      source,
      statusLabel,
      url,
      urlError,
      urlInput,
      validateUrl,
    };
  },
};
</script>

<style scoped>
.url-input {
  display: flex;
  min-height: 46px;
  align-items: center;
  padding: 0 12px;
  border: 1px solid #cfd6df;
  border-radius: 8px;
}
.url-input:focus-within {
  border-color: #4aa487;
  box-shadow: 0 0 0 3px rgba(22, 138, 100, 0.1);
}
.url-input.error {
  border-color: #dc6964;
}
.url-input.valid {
  border-color: #72bba3;
}
.url-input input {
  min-width: 0;
  flex: 1;
  padding: 0;
  border: 0;
  border-radius: 0;
  outline: 0;
  color: #273244;
  font-size: 14px;
  font-weight: 400;
}
.url-input span {
  padding: 5px 8px;
  border-radius: 6px;
  background: #fff0f4;
  color: #9e3150;
  font-size: 11px;
  font-weight: 700;
}
.alert-summary {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 15px 16px;
  border: 1px solid #dfe5eb;
  border-radius: 9px;
  background: #fff;
}
.alert-icon {
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  border-radius: 8px;
  background: #e9f7f1;
  color: #168a64;
}
.alert-summary > div {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 3px;
  font-size: 13px;
}
.alert-summary small {
  color: #7d8795;
}
.alert-summary em {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  border-radius: 6px;
  background: #eaf7f2;
  color: #21755c;
  font-size: 11px;
  font-style: normal;
  font-weight: 700;
}
.alert-summary em i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #27a979;
}
.alert-summary em.off {
  background: #fff5e8;
  color: #7a4a10;
}
.alert-summary em.off i {
  background: #d79b2b;
}

/* Ajouts de la session (analyse en arrière-plan) */
.queue {
  padding: 20px 24px;
  border: 1px solid #e3e8ef;
  border-radius: 12px;
  background: #fff;
}
.queue__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.queue__header h2 {
  margin: 0;
  font-family: "Manrope", sans-serif;
  font-size: 15px;
}
.queue__clear {
  border: 0;
  background: none;
  color: #657184;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.queue__hint {
  margin: 6px 0 14px;
  color: #7d8795;
  font-size: 12px;
}
.queue ul {
  margin: 0;
  padding: 0;
  list-style: none;
}
.queue__item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-top: 1px solid #edf0f4;
}
.queue__dot {
  width: 8px;
  height: 8px;
  flex: 0 0 8px;
  border-radius: 50%;
  background: #c0c8d2;
}
.queue__item--pending .queue__dot {
  background: #d79b2b;
  animation: queue-pulse 1s ease-in-out infinite;
}
.queue__item--done .queue__dot {
  background: #27a979;
}
.queue__item--failed .queue__dot {
  background: #c84642;
}
.queue__body {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 3px;
}
.queue__body strong {
  overflow: hidden;
  color: #283446;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.queue__body small {
  color: #8a4b47;
  font-size: 12px;
}
.queue__item--timeout .queue__body small {
  color: #7d8795;
}
.queue__status {
  color: #596577;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}
.queue__item--done .queue__status {
  color: #1f7a5a;
}
.queue__item--failed .queue__status {
  color: #c84642;
}
.queue__link {
  display: inline-block;
  margin-top: 12px;
  color: #2e6098;
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
}
@keyframes queue-pulse {
  50% {
    opacity: 0.35;
  }
}
</style>
